import { useMemo } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { DevicesApi, DevicesQueryKey } from '@frontend/api-devices';
import { useTranslation } from '@frontend/i18n';
import { useFormField, useAlert } from '@frontend/design-system';
import { useSoftphoneUpdateCwiCache } from '../../hooks/use-softphone-update-cwi-cache';
import { useSoftphoneSettings } from '../../providers/softphone-settings-provider';
import { SettingItem } from './setting-item';

export const CallWaitingIndicatorToggle = () => {
  const alerts = useAlert();
  const { t } = useTranslation('softphone');
  const isCallWaitingIndicatorBeepEnabled = useSoftphoneSettings((ctx) => ctx.isCallWaitingIndicatorBeepEnabled);
  const device = useSoftphoneSettings((ctx) => ctx.device);
  const queryClient = useQueryClient();
  const phoneConfigTenantId = useSoftphoneSettings((ctx) => ctx.phoneConfigTenantId);
  const { updateCwiSoftphoneCache, queryKey } = useSoftphoneUpdateCwiCache(phoneConfigTenantId);
  const switchProps = useFormField({
    type: 'switch',
    value: isCallWaitingIndicatorBeepEnabled,
  });

  const mutateDevice = useMutation(
    async ({ value }: { value: boolean }) => {
      if (device) {
        await DevicesApi.UpdateDevice({
          device: {
            ...device,
            callWaitingIndicatorBeep: value,
          },
        });
      }
    },
    {
      onMutate: async (variables) => {
        return updateCwiSoftphoneCache({
          deviceId: device?.deviceId || '',
          enabled: variables.value,
        });
      },
      onSuccess: () => {
        queryClient.invalidateQueries([device?.deviceId, ...DevicesQueryKey.queryKeys.deviceById()]);
        queryClient.invalidateQueries(queryKey);

        alerts.success(t('Saved Ring Settings'));
      },
      onError: (_e, _v, context) => {
        if (context?.oldData) {
          queryClient.setQueryData(queryKey, context.oldData);
        }
        alerts.error(t('Unable to save Ring Settings. Please try again.'));
      },
    }
  );

  const _switchProps = useMemo(
    () => ({
      ...switchProps,
      onChange: (event: { name: string; value: boolean }) => {
        const value = event?.value;
        switchProps.onChange(event);
        mutateDevice.mutate({
          value,
        });
      },
    }),
    [switchProps, mutateDevice]
  );

  return (
    <SettingItem
      // @ts-ignore - field type issues
      switchProps={_switchProps}
      title={t('Call Waiting Indicator Beep')}
      helperText={t('You will hear a beep when an incoming call is received while you are on an active call.')}
    />
  );
};
